import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Stack,
  Text,
  TextInput,
  Button,
  Group,
  Paper,
  Badge,
  Progress,
  Alert,
  useMantineColorScheme
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconX,
  IconCheck,
  IconInfoCircle,
  IconFileTypePdf,
  IconFileTypeDocx,
  IconFileTypeXls,
  IconFileText
} from '@tabler/icons-react';
import { aiKnowledgeApi } from '@/api/aiKnowledge.api';

interface UploadDocumentModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadDocumentModal = ({ opened, onClose, onSuccess }: UploadDocumentModalProps) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/plain'
  ];

  const handleFileSelect = (selectedFile: File) => {
    if (!allowedTypes.includes(selectedFile.type)) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.documents.unsupportedFormat'),
        color: 'red'
      });
      return;
    }

    if (selectedFile.size > 50 * 1024 * 1024) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.documents.fileTooLarge'),
        color: 'red'
      });
      return;
    }

    setFile(selectedFile);
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title || file.name.replace(/\.[^.]+$/, ''));

    try {
      setUploading(true);
      setUploadProgress(0);

      const response = await aiKnowledgeApi.uploadDocument(formData, (percent) => {
        setUploadProgress(percent);
      });

      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: t('aiKnowledge.documents.uploadSuccess'),
          color: 'green',
          icon: <IconCheck size={18} />
        });
        resetForm();
        onSuccess();
      }
    } catch (error) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.uploadFailed'),
        color: 'red'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setFile(null);
    setTitle('');
    setUploadProgress(0);
  };

  const handleClose = () => {
    if (!uploading) {
      resetForm();
      onClose();
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return <IconFileTypePdf size={32} color="var(--mantine-color-red-5)" />;
      case 'docx': case 'doc': return <IconFileTypeDocx size={32} color="var(--mantine-color-blue-5)" />;
      case 'xlsx': case 'xls': return <IconFileTypeXls size={32} color="var(--mantine-color-green-5)" />;
      default: return <IconFileText size={32} color="var(--mantine-color-gray-5)" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('aiKnowledge.documents.uploadTitle')}
      size="md"
    >
      <Stack gap="md">
        {/* Drop zone */}
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: dragOver
              ? 'var(--mantine-color-blue-5)'
              : file
                ? 'var(--mantine-color-green-5)'
                : colorScheme === 'dark'
                  ? 'var(--mantine-color-dark-4)'
                  : 'var(--mantine-color-gray-4)',
            background: dragOver
              ? colorScheme === 'dark'
                ? 'rgba(59, 130, 246, 0.1)'
                : 'rgba(59, 130, 246, 0.05)'
              : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc,.xlsx,.xls,.txt"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />

          {file ? (
            <Group justify="center" gap="md">
              {getFileIcon(file.name)}
              <div>
                <Text fw={500} size="sm">{file.name}</Text>
                <Text size="xs" c="dimmed">{formatFileSize(file.size)}</Text>
              </div>
              <ActionIconX
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setFile(null);
                }}
              />
            </Group>
          ) : (
            <Stack align="center" gap="xs">
              <IconUpload size={40} color="var(--mantine-color-dimmed)" />
              <Text fw={500} size="sm" ta="center">
                {t('aiKnowledge.documents.dropzone')}
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                {t('aiKnowledge.documents.dropzoneHint')}
              </Text>
            </Stack>
          )}
        </Paper>

        {/* Supported formats */}
        <Group gap="xs" justify="center">
          <Badge variant="light" color="red" size="sm">PDF</Badge>
          <Badge variant="light" color="blue" size="sm">DOCX</Badge>
          <Badge variant="light" color="green" size="sm">XLSX</Badge>
          <Badge variant="light" color="gray" size="sm">TXT</Badge>
        </Group>

        {/* Title input */}
        <TextInput
          label={t('aiKnowledge.documents.titleLabel')}
          placeholder={t('aiKnowledge.documents.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        {/* Upload progress */}
        {uploading && (
          <Progress
            value={uploadProgress}
            animated
            size="lg"
            radius="md"
            color="blue"
          />
        )}

        {/* Info */}
        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="xs">
          <Text size="xs">{t('aiKnowledge.documents.uploadInfo')}</Text>
        </Alert>

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={uploading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpload}
            loading={uploading}
            disabled={!file}
            leftSection={<IconUpload size={16} />}
            variant="gradient"
            gradient={{ from: 'blue', to: 'violet' }}
          >
            {t('aiKnowledge.documents.uploadButton')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

// Simple inline close button to avoid unused import
const ActionIconX = ({ onClick }: { onClick: (e: React.MouseEvent) => void }) => (
  <button
    onClick={onClick}
    style={{
      background: 'none',
      border: '1px solid var(--mantine-color-gray-4)',
      borderRadius: 4,
      cursor: 'pointer',
      padding: 4,
      display: 'flex',
      alignItems: 'center'
    }}
  >
    <IconX size={16} />
  </button>
);

export default UploadDocumentModal;
